import type { ReactNode } from 'react';
import { ClipboardPaste, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Switch } from '@/app/components/ui/switch';
import { TabsContent } from '@/app/components/ui/tabs';
import type { KeywordItem } from '@/lib/api';
import { CharBar } from '../CharBar';
import { addVariant, removeVariant, updateVariant } from '../useTemplateEditor';
import type { EditorReady } from './types';

type Props = {
  ed: EditorReady;
  fieldPalette: ReactNode;
  rowPicker: ReactNode;
  keywordCount: number;
};

export function KeywordsTab({ ed, fieldPalette, rowPicker, keywordCount }: Props) {
  const {
    templateSet, cities,
    expandedVariants, setExpandedVariants,
    updateTemplateContent, handleKeywordPaste, pasteKeywords,
    parseKeywordMatchType, wrapKeyword, stripKeywordWrap,
    inputProps, getPreviewText, getCharInfo,
  } = ed;

  return (
    <TabsContent value="keywords" className="space-y-4">
      {fieldPalette}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">{keywordCount} keyword template{keywordCount !== 1 ? 's' : ''}</p>
          {cities.length > 0 && rowPicker}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="text-[11px] font-mono text-muted-foreground/70 hover:text-primary border border-border/50 hover:border-primary/40 hover:bg-primary/5 rounded px-2 py-1 transition-colors flex items-center gap-1.5"
            title="Paste keywords from clipboard (one per line)"
            onClick={pasteKeywords}
          ><ClipboardPaste className="size-3" />Paste</button>
          <Button size="sm" onClick={() => {
            const newKw: KeywordItem = {
              id: crypto.randomUUID(), item_name: '',
              match_types: ['phrase'],
              variants: [{ variant_order: 1, template_string: '', enabled: true }],
            };
            updateTemplateContent('keywords', [...templateSet.template_content.keywords, newKw]);
          }}>
            <Plus className="size-3.5 mr-1.5" />Add Keyword
          </Button>
        </div>
      </div>

      {templateSet.template_content.keywords.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-14 text-center">
          <p className="text-sm text-muted-foreground">No keyword templates yet</p>
          <p className="text-xs text-muted-foreground/50 mt-1">Each template generates one keyword row per data row</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid gap-3 px-3 py-1.5 bg-muted/40 border-b border-border text-[10px] font-mono text-muted-foreground uppercase tracking-widest"
            style={{ gridTemplateColumns: '130px 1.2fr 80px 1fr 72px 44px' }}>
            <span>Label</span><span>Template</span><span>Match</span><span>Preview</span><span>Chars</span><span />
          </div>
          {templateSet.template_content.keywords.map((keyword) => {
            const primary = keyword.variants[0];
            const extras = keyword.variants.slice(1);
            const isExpanded = expandedVariants[`k:${keyword.id}`] ?? false;
            return (
              <div key={keyword.id} className="border-b border-border/40 last:border-0">
                <div className="grid gap-3 px-3 py-1.5 items-center group hover:bg-muted/20 transition-colors"
                  style={{ gridTemplateColumns: '130px 1.2fr 80px 1fr 72px 44px' }}>
                  <Input
                    value={keyword.item_name ?? ''}
                    onChange={e => updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => k.id === keyword.id ? { ...k, item_name: e.target.value } : k))}
                    placeholder="Label…"
                    className="h-7 text-xs border-0 border-b border-border/40 rounded-none bg-transparent focus-visible:ring-0 px-0 text-muted-foreground"
                  />
                  <Input
                    value={primary?.template_string ?? ''}
                    onChange={e => {
                      const newText = e.target.value;
                      const detected = parseKeywordMatchType(newText);
                      updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => {
                        if (k.id !== keyword.id) return k;
                        return {
                          ...k,
                          match_types: detected.matchTypes,
                          variants: updateVariant(k.variants, 1, { template_string: newText }),
                        };
                      }));
                    }}
                    onPaste={e => handleKeywordPaste(e, keyword.id)}
                    placeholder="e.g., dentiste {nom_standard}"
                    className="h-7 text-sm border-0 border-b border-border/40 rounded-none bg-transparent focus-visible:ring-0 px-0"
                    {...inputProps(`k:${keyword.id}:1`)}
                  />
                  <div className="flex gap-1">
                    {(['exact', 'phrase', 'broad'] as const).map(mt => (
                      <button key={mt}
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded border transition-colors ${keyword.match_types.includes(mt) ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-transparent border-border/40 text-muted-foreground/40 hover:border-border'}`}
                        onClick={() => {
                          const types = keyword.match_types.includes(mt)
                            ? keyword.match_types.filter(t => t !== mt)
                            : [...keyword.match_types, mt];
                          if (types.length === 0) return;
                          const newPrimaryMt = types.includes('exact') ? 'exact' : types.includes('phrase') ? 'phrase' : 'broad';
                          const currentText = keyword.variants[0]?.template_string ?? '';
                          const wrapped = wrapKeyword(currentText, newPrimaryMt);
                          updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => {
                            if (k.id !== keyword.id) return k;
                            return {
                              ...k,
                              match_types: types,
                              variants: k.variants.map((v, i) => i === 0 ? { ...v, template_string: wrapped } : v),
                            };
                          }));
                        }}>
                        {mt[0].toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-primary/70" title={getPreviewText(primary?.template_string ?? '')}>
                    {getPreviewText(primary?.template_string ?? '') || <span className="text-muted-foreground/30 italic">—</span>}
                  </div>
                  <CharBar {...getCharInfo(stripKeywordWrap(primary?.template_string ?? ''), 80)} limit={80} />
                  <div className="flex items-center gap-0.5">
                    <button
                      className="size-6 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        const key = `k:${keyword.id}`;
                        if (!isExpanded && extras.length === 0) {
                          updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => k.id === keyword.id ? { ...k, variants: addVariant(k.variants) } : k));
                        }
                        setExpandedVariants(prev => ({ ...prev, [key]: !prev[key] }));
                      }}
                    >
                      {extras.length > 0 ? <span className="text-[9px] font-mono text-primary/60">+{extras.length}</span> : <span className="text-[12px] opacity-30 group-hover:opacity-70">⊕</span>}
                    </button>
                    <button
                      className="size-6 flex items-center justify-center rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                      onClick={() => updateTemplateContent('keywords', templateSet.template_content.keywords.filter(k => k.id !== keyword.id))}
                    ><Trash2 className="size-3" /></button>
                  </div>
                </div>
                {isExpanded && (
                  <>
                    {extras.map((v) => (
                      <div key={v.variant_order}
                        className="grid gap-3 px-3 py-1.5 items-center bg-muted/10 border-t border-dashed border-border/40"
                        style={{ gridTemplateColumns: '130px 1.2fr 80px 1fr 72px 44px' }}>
                        <span className="text-[10px] font-mono text-muted-foreground/50">V{v.variant_order}</span>
                        <Input
                          value={v.template_string}
                          onChange={e => updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => {
                            if (k.id !== keyword.id) return k;
                            return { ...k, variants: updateVariant(k.variants, v.variant_order, { template_string: e.target.value }) };
                          }))}
                          placeholder="Fallback…"
                          className="h-7 text-sm border-0 border-b border-dashed border-border/40 rounded-none bg-transparent focus-visible:ring-0 px-0"
                          {...inputProps(`k:${keyword.id}:${v.variant_order}`)}
                        />
                        <span />
                        <div className="text-xs text-primary/60 truncate">{getPreviewText(v.template_string)}</div>
                        <CharBar {...getCharInfo(v.template_string, 80)} limit={80} />
                        <button
                          className="size-6 flex items-center justify-center rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                          onClick={() => updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => {
                            if (k.id !== keyword.id) return k;
                            return { ...k, variants: removeVariant(k.variants, v.variant_order) };
                          }))}
                        ><Trash2 className="size-3" /></button>
                      </div>
                    ))}
                    <div className="px-3 py-1 bg-muted/10 border-t border-dashed border-border/30">
                      <button className="text-xs font-mono text-muted-foreground/50 hover:text-primary transition-colors"
                        onClick={() => updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => k.id === keyword.id ? { ...k, variants: addVariant(k.variants) } : k))}>
                        + add fallback
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-muted/10">
        <div>
          <p className="text-sm font-medium">Accent-stripped duplicates</p>
          <p className="text-xs text-muted-foreground">Generates extra keywords without accents (é→e, à→a)</p>
        </div>
        <Switch
          checked={templateSet.template_content.generate_accent_stripped_keywords}
          onCheckedChange={checked => updateTemplateContent('generate_accent_stripped_keywords', checked)}
        />
      </div>
    </TabsContent>
  );
}
